import {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import styled from "styled-components";
import { gql, useQuery, useMutation } from "@apollo/client";
import { trackBulkDownload } from "../util/downloadUtils";
import { QuarantineStatus } from "../generated/graphql";
import { useNotifications } from "../contexts/NotificationContext";
import { isSessionExpiredError } from "../contexts/SessionExpiredContext";
import { createQuarantineNotification } from "./QuarantineNotifications";
import UploadedFilesGrid from "./UploadedFilesGrid";
import ConfirmDialog from "./ConfirmDialog";

const ALL_FILES_QUERY = gql`
  query GetAllFiles {
    allFiles {
      id
      fileName
      extension
      createdAt
      fileUrl
      clientId
      folderPath
      subfolderPath
      reviewedByCurrentUser
      quarantineStatus
      quarantineReason
      quarantineTimestamp
      scanEngine
      scanEngineVersion
      scanStartedAt
      scanCompletedAt
      client {
        clientId
        client_name
      }
    }
  }
`;

const FILES_BY_CLIENT_QUERY = gql`
  query GetFilesByClient($clientId: ID!) {
    filesByClient(clientId: $clientId) {
      id
      fileName
      extension
      createdAt
      fileUrl
      clientId
      folderPath
      subfolderPath
      reviewedByCurrentUser
      quarantineStatus
      quarantineReason
      quarantineTimestamp
      scanEngine
      scanEngineVersion
      scanStartedAt
      scanCompletedAt
      client {
        clientId
        client_name
      }
    }
  }
`;

const DELETE_FILE_MUTATION = gql`
  mutation DeleteFile($id: ID!) {
    deleteFile(id: $id)
  }
`;

const TOGGLE_FILE_REVIEW_MUTATION = gql`
  mutation ToggleFileReview($fileId: ID!) {
    toggleFileReview(fileId: $fileId) {
      id
      fileName
      extension
      createdAt
      clientId
      reviewedByCurrentUser
    }
  }
`;

// Use root-level mutation so non-admin users can request their own client's zip
const GET_CLIENT_FILES_ZIP_URL = gql`
  mutation GetClientFilesZipUrl($clientId: ID!) {
    getClientFilesZipUrl(clientId: $clientId) {
      status
      downloadUrl
      fileCount
      estimatedSize
      error
    }
  }
`;

const FileListContainer = styled.div`
  margin: 20px 0;
`;

const FileListTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 15px;
`;

const FileTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 10px;
  border-bottom: 2px solid #ddd;
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  &:hover {
    background-color: #f1f1f1;
  }
`;

const TableCell = styled.td`
  padding: 10px;
  border-bottom: 1px solid #ddd;
`;

const FileLink = styled.a`
  color: #007bff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #c82333;
  }
`;

const ReviewCheckbox = styled.input.attrs({ type: "checkbox" })`
  cursor: pointer;
  margin-left: 8px;
  width: 18px;
  height: 18px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const Loading = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #dc3545;
`;

const FileListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const DownloadAllLink = styled.a`
  color: #4169e1;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  transition: opacity 0.2s;

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }

  &:disabled,
  &[aria-disabled="true"] {
    color: #999;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const FolderPath = styled.span`
  font-weight: bold;
`;

const SubfolderPath = styled.span`
  color: #666;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
`;

const StatusBadge = styled.span<{ status: QuarantineStatus }>`
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: white;
  background-color: ${(props) => {
    switch (props.status) {
      case QuarantineStatus.Clean:
        return "#28a745";
      case QuarantineStatus.Quarantined:
        return "#dc3545";
      case QuarantineStatus.ScanFailed:
        return "#fd7e14";
      default:
        return "#6c757d";
    }
  }};
`;

const FileNameWithStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface FileData {
  id: string;
  fileName: string;
  extension: string;
  createdAt: string;
  fileUrl: string | null;
  clientId: string;
  folderPath: string | null;
  subfolderPath: string | null;
  reviewedByCurrentUser?: boolean;
  quarantineStatus: QuarantineStatus;
  quarantineReason?: string | null;
  quarantineTimestamp?: string | null;
  scanEngine?: string | null;
  scanEngineVersion?: string | null;
  scanStartedAt?: string | null;
  scanCompletedAt?: string | null;
  client: {
    clientId: string;
    client_name: string;
  };
}

export interface FileListRefType {
  refetch: () => Promise<any>;
  addNewFile: (fileData: any) => void;
}

interface UserData {
  id: string;
  email: string | null;
  name: string;
  role: string;
  clientId: string | null;
  client?: {
    clientId: string;
    client_name: string;
    folder: string;
    upload_complete: boolean;
  } | null;
  supplierId?: string | null;
  supplier?: {
    id: string;
    name: string;
    subfolder: string;
  } | null;
}

interface FileListProps {
  clientId?: string;
  userData?: UserData;
}

const FileList = forwardRef<FileListRefType, FileListProps>(
  ({ clientId, userData }, ref) => {
    // Use the appropriate query based on whether a clientId is provided
    const queryToUse = clientId ? FILES_BY_CLIENT_QUERY : ALL_FILES_QUERY;
    const variables = clientId ? { clientId } : {};
    // AG Grid is now the default - set VITE_USE_AG_GRID=false to use HTML table
    const useAgGrid = import.meta.env.VITE_USE_AG_GRID !== "false";
    const [filteredFiles, setFilteredFiles] = useState<FileData[]>([]);
    const [optimisticFiles, setOptimisticFiles] = useState<FileData[]>([]);
    const refetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // State for custom delete confirmation dialog
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{
      id: string;
      name: string;
    } | null>(null);

    // Notification system for quarantine alerts
    const { addNotification } = useNotifications();

    // Track previous quarantine statuses to detect changes
    const previousQuarantineStatusesRef = useRef<Map<string, QuarantineStatus>>(
      new Map(),
    );

    const { loading, error, data, refetch } = useQuery(queryToUse, {
      variables,
      fetchPolicy: "cache-and-network", // Use cache for faster initial renders, but also fetch from network
    });

    const [deleteFile] = useMutation(DELETE_FILE_MUTATION);
    const [toggleFileReview] = useMutation(TOGGLE_FILE_REVIEW_MUTATION);
    const [getZipUrl, { loading: downloadLoading }] = useMutation(
      GET_CLIENT_FILES_ZIP_URL,
    );

    // Debounced refetch to prevent too many requests
    const debouncedRefetch = useCallback(async () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      refetchTimeoutRef.current = setTimeout(async () => {
        await refetch({ fetchPolicy: "network-only" });
      }, 300); // 300ms debounce
    }, [refetch]);

    // Add new file optimistically (for immediate UI feedback)
    const addNewFile = useCallback(
      (fileData: any) => {
        // Convert the fileData to match our FileData interface
        const newFile: FileData = {
          id: fileData.id,
          fileName: fileData.fileName,
          extension: fileData.extension,
          createdAt: fileData.createdAt,
          fileUrl: fileData.fileUrl,
          clientId: fileData.clientId,
          folderPath: fileData.folderPath || null,
          subfolderPath: fileData.subfolderPath || null,
          reviewedByCurrentUser: false,
          quarantineStatus: fileData.quarantineStatus || QuarantineStatus.Clean,
          quarantineReason: fileData.quarantineReason || null,
          quarantineTimestamp: fileData.quarantineTimestamp || null,
          scanEngine: fileData.scanEngine || null,
          scanEngineVersion: fileData.scanEngineVersion || null,
          scanStartedAt: fileData.scanStartedAt || null,
          scanCompletedAt: fileData.scanCompletedAt || null,
          client: fileData.client,
        };

        // Add to optimistic files (these persist through data refetches)
        setOptimisticFiles((prev) => [newFile, ...prev]);

        // Schedule a background refetch to ensure data consistency
        debouncedRefetch();
      },
      [debouncedRefetch],
    );

    // Expose refetch method and addNewFile to parent components
    useImperativeHandle(ref, () => ({
      refetch: async () => {
        return refetch({ fetchPolicy: "network-only" });
      },
      addNewFile,
    }));

    // Filter files based on user permissions
    useEffect(() => {
      if (!data) return;

      // Get files from the appropriate query result
      const files: FileData[] = clientId
        ? data.filesByClient || []
        : data.allFiles || [];

      if (!userData || userData.role === "ADMIN") {
        // Admin users can see all files
        setFilteredFiles(files);
        return;
      }

      const permittedFolders: { folder: string; subfolder?: string }[] = [];

      // For supplier users, they can only see files in their assigned subfolder
      if (userData.role === "SUPPLIER") {
        // Use supplier data from userData
        if (userData.supplier && userData.clientId) {
          permittedFolders.push({
            folder: userData.clientId.toString(),
            subfolder: userData.supplier.subfolder,
          });

          // Also add client folder as we might have files with client folder name
          if (userData.client && userData.client.folder) {
            permittedFolders.push({
              folder: userData.client.folder,
              subfolder: userData.supplier.subfolder,
            });
          }
        }
      }
      // Portway and Customer users can see all files in the client folder
      else if (
        userData.role === "PORTWAY_EMPLOYEE" ||
        userData.role === "CUSTOMER"
      ) {
        // Users should see all files for their client, regardless of subfolder
        if (userData.clientId) {
          // Add permission by clientId
          permittedFolders.push({
            folder: userData.clientId.toString(),
            subfolder: undefined, // No subfolder restriction
          });

          // Also add by client folder name if available
          if (userData.client && userData.client.folder) {
            permittedFolders.push({
              folder: userData.client.folder,
              subfolder: undefined, // No subfolder restriction
            });
          }
        }
      }

      // Check if we have any permitted folders, if not, show all files (fallback)
      if (permittedFolders.length === 0 && userData.clientId) {
        permittedFolders.push({ folder: userData.clientId.toString() });
      }

      // Filter files based on folder permissions
      const filtered = files.filter((file: FileData) => {
        // If no folder path, show the file
        if (!file.folderPath) {
          return true;
        }

        // Get client information from the file to handle both folder name and clientId formats
        const fileClientId = file.clientId;

        // Check if file's folder matches any permitted folder
        const isPermitted = permittedFolders.some((permitted) => {
          // First check if permitted folder matches folderPath directly
          const folderMatches = permitted.folder === file.folderPath;

          // Also check if user's clientId matches file's clientId
          const clientIdMatches =
            userData &&
            userData.clientId &&
            userData.clientId.toString() === fileClientId;

          // If folder matches OR client IDs match, then check subfolder restrictions
          if (folderMatches || clientIdMatches) {
            // Customer and Portway Employee can see all files regardless of subfolder
            if (
              userData.role === "CUSTOMER" ||
              userData.role === "PORTWAY_EMPLOYEE"
            ) {
              return true;
            }

            // For SUPPLIER users, enforce subfolder restriction
            if (userData.role === "SUPPLIER") {
              // If a specific subfolder is required but not matching
              if (
                permitted.subfolder &&
                file.subfolderPath !== permitted.subfolder
              ) {
                return false;
              }
              return true;
            }

            // For other roles or no role specified
            return true;
          }
          return false;
        });

        return isPermitted;
      });

      // Remove optimistic files that now exist in server data (to avoid duplicates)
      const serverFileIds = new Set(filtered.map((f) => f.id));
      const remainingOptimisticFiles = optimisticFiles.filter(
        (f) => !serverFileIds.has(f.id),
      );

      // Update optimistic files state to remove duplicates
      if (remainingOptimisticFiles.length !== optimisticFiles.length) {
        setOptimisticFiles(remainingOptimisticFiles);
      }

      // Merge optimistic files (at the top) with server files
      const mergedFiles = [...remainingOptimisticFiles, ...filtered];

      setFilteredFiles(mergedFiles);
    }, [data, userData, clientId, optimisticFiles]);

    // Polling logic for malware scanning
    // Refetch data every 5 seconds while any file is being scanned
    useEffect(() => {
      // Check if any file is currently being scanned (has scanStartedAt but not scanCompletedAt)
      const hasScanning = filteredFiles.some(
        (file) => file.scanStartedAt && !file.scanCompletedAt,
      );

      if (!hasScanning) {
        // No files are being scanned, stop polling
        return;
      }

      // Start polling every 5 seconds
      const pollInterval = setInterval(() => {
        refetch({ fetchPolicy: "network-only" });
      }, 5000);

      // Cleanup interval on unmount or when scanning completes
      return () => clearInterval(pollInterval);
    }, [filteredFiles, refetch]);

    // Detect quarantine status changes and show notifications
    useEffect(() => {
      const previousStatuses = previousQuarantineStatusesRef.current;

      filteredFiles.forEach((file) => {
        const previousStatus = previousStatuses.get(file.id);

        // Only check if we have a previous status (not first load)
        if (previousStatus !== undefined) {
          // Check if file was just quarantined
          if (
            previousStatus !== QuarantineStatus.Quarantined &&
            file.quarantineStatus === QuarantineStatus.Quarantined
          ) {
            // Show quarantine notification
            const notification = createQuarantineNotification(
              file.fileName,
              QuarantineStatus.Quarantined,
              file.quarantineReason || undefined,
            );
            addNotification(notification);
          }
          // Check if scan just failed
          else if (
            previousStatus !== QuarantineStatus.ScanFailed &&
            file.quarantineStatus === QuarantineStatus.ScanFailed
          ) {
            // Show scan failed notification
            const notification = createQuarantineNotification(
              file.fileName,
              QuarantineStatus.ScanFailed,
            );
            addNotification(notification);
          }
        }

        // Update the previous status
        previousStatuses.set(file.id, file.quarantineStatus);
      });

      // Clean up entries for files that no longer exist
      const currentFileIds = new Set(filteredFiles.map((f) => f.id));
      Array.from(previousStatuses.keys()).forEach((fileId) => {
        if (!currentFileIds.has(fileId)) {
          previousStatuses.delete(fileId);
        }
      });
    }, [filteredFiles, addNotification]);

    // Check if user can delete files
    const canDeleteFiles =
      userData &&
      (userData.role === "ADMIN" ||
        userData.role === "CUSTOMER" ||
        userData.role === "SUPPLIER");

    // Check if user is PORTWAY_EMPLOYEE
    const isPortwayEmployee = userData?.role === "PORTWAY_EMPLOYEE";

    const handleDeleteFile = (id: string) => {
      // Find the file to get its name for the confirmation message
      const file = filteredFiles.find((f) => f.id === id);
      const fileName = file?.fileName || "this file";

      // Show custom confirmation dialog
      setFileToDelete({ id, name: fileName });
      setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
      if (!fileToDelete) return;

      const { id } = fileToDelete;
      setShowDeleteDialog(false);

      try {
        // Remove from optimistic files immediately for instant UI feedback
        setOptimisticFiles((prev) => prev.filter((file) => file.id !== id));

        await deleteFile({
          variables: { id },
          // Force Apollo to bypass cache and update data from server
          update: (cache) => {
            // Invalidate and remove this file from the cache
            cache.evict({ id: `FileData:${id}` });
            cache.gc();
          },
          // Force refetch after deletion to get updated data
          refetchQueries: [
            {
              query: queryToUse,
              variables,
              fetchPolicy: "network-only",
            },
          ],
        });

        // Explicitly refetch from server to ensure UI updates
        await refetch({ fetchPolicy: "network-only" });
        setFileToDelete(null);
      } catch (error) {
        console.error("Error deleting file:", error);

        // If deletion failed, restore the file to optimistic files if it was there
        const fileToRestore = filteredFiles.find((f) => f.id === id);
        if (fileToRestore && !filteredFiles.some((f) => f.id === id)) {
          setOptimisticFiles((prev) => [fileToRestore, ...prev]);
        }

        // Skip alert when session-expired modal will handle the error
        if (!isSessionExpiredError()) {
          alert("Failed to delete the file. Please try again.");
        }
        setFileToDelete(null);
      }
    };

    const handleToggleReview = async (fileId: string) => {
      // Find the file in our local state
      const fileToUpdate = filteredFiles.find((file) => file.id === fileId);
      if (!fileToUpdate) return;

      // Create a new array with the updated file to avoid mutation
      const updatedFiles = filteredFiles.map((file) => {
        if (file.id === fileId) {
          // Toggle the review status optimistically
          return {
            ...file,
            reviewedByCurrentUser: !file.reviewedByCurrentUser,
          };
        }
        return file;
      });

      // Update the local state first for immediate UI feedback
      setFilteredFiles(updatedFiles);

      try {
        // Send the mutation to the server
        const result = await toggleFileReview({
          variables: { fileId },
        });

        // If the mutation fails or returns a different result than expected,
        // refetch to ensure our UI is consistent with the server
        if (
          !result.data?.toggleFileReview ||
          result.data.toggleFileReview.reviewedByCurrentUser !==
            !fileToUpdate.reviewedByCurrentUser
        ) {
          await refetch({ fetchPolicy: "network-only" });
        }
      } catch (error) {
        console.error("Error toggling file review status:", error);

        // In case of error, revert to the original state
        setFilteredFiles(filteredFiles);

        // Skip alert when session-expired modal will handle the error
        if (!isSessionExpiredError()) {
          alert("Failed to update the review status. Please try again.");
        }
      }
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleString();
    };

    const formatLocation = (file: FileData) => {
      if (!file.folderPath) return "—";
      if (file.subfolderPath) {
        return (
          <>
            <FolderPath>{file.folderPath}</FolderPath>/{" "}
            <SubfolderPath>{file.subfolderPath}</SubfolderPath>
          </>
        );
      }
      return <FolderPath>{file.folderPath}</FolderPath>;
    };

    // Check if user can use bulk download (ADMIN, CUSTOMER, PORTWAY_EMPLOYEE)
    const canUseBulkDownload =
      userData?.role === "ADMIN" ||
      userData?.role === "CUSTOMER" ||
      userData?.role === "PORTWAY_EMPLOYEE";

    const handleDownloadAll = async () => {
      if (!clientId) return;

      try {
        const response = await getZipUrl({
          variables: { clientId },
        });

        const result = response.data?.getClientFilesZipUrl;

        if (result?.status === "ready" && result.downloadUrl) {
          // Track the download first
          trackBulkDownload({
            clientId,
            fileCount: result.fileCount || 0,
            zipUrl: result.downloadUrl,
          });

          // Handle relative URLs - convert to absolute URL if needed
          let downloadUrl = result.downloadUrl;

          // Check if we're in local development and if the URL is relative
          if (downloadUrl.startsWith("/")) {
            // In development, the backend runs on a configurable port
            if (window.location.hostname === "localhost") {
              const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
              downloadUrl = `http://localhost:${backendPort}${downloadUrl}`;
            } else {
              // In production, use the same hostname but with the relative path
              downloadUrl = `${window.location.origin}${downloadUrl}`;
            }
          }

          // Open in a new tab to avoid navigation issues
          window.open(downloadUrl, "_blank");
        } else if (result?.error) {
          alert(`Error preparing download: ${result.error}`);
        } else {
          alert(
            "Unable to prepare files for download. Please try again later.",
          );
        }
      } catch (error) {
        console.error("Error preparing download:", error);
        if (!isSessionExpiredError()) {
          alert("Failed to prepare download. Please try again.");
        }
      }
    };

    if (loading) return <Loading>Loading files...</Loading>;
    if (error)
      return <ErrorMessage>Error loading files: {error.message}</ErrorMessage>;

    return (
      <FileListContainer data-testid="file-list">
        <FileListHeader>
          <FileListTitle>Uploaded Files</FileListTitle>
          {clientId && canUseBulkDownload && filteredFiles.length > 0 && (
            <DownloadAllLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!downloadLoading && filteredFiles.length > 0) {
                  handleDownloadAll();
                }
              }}
              aria-disabled={downloadLoading || filteredFiles.length === 0}
              data-testid="download-all-files-button"
            >
              {downloadLoading ? "Preparing..." : "Download All Files"}
            </DownloadAllLink>
          )}
        </FileListHeader>
        {filteredFiles.length === 0 ? (
          <EmptyMessage>No files found</EmptyMessage>
        ) : useAgGrid ? (
          <UploadedFilesGrid
            rows={filteredFiles.filter(
              (file) => file.quarantineStatus !== QuarantineStatus.Quarantined,
            )}
            canDelete={!!canDeleteFiles}
            showReviewed={!!isPortwayEmployee}
            onDelete={handleDeleteFile}
            onToggleReview={handleToggleReview}
          />
        ) : (
          <FileTable data-testid="file-list-table">
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Upload Date</TableHeader>
                <TableHeader>Location (Folder/Subfolder)</TableHeader>
                <TableHeader>Actions</TableHeader>
                {isPortwayEmployee && <TableHeader>Reviewed</TableHeader>}
              </tr>
            </thead>
            <tbody>
              {filteredFiles
                // Filter out quarantined files from display
                .filter(
                  (file) =>
                    file.quarantineStatus !== QuarantineStatus.Quarantined,
                )
                .map((file) => (
                  <TableRow key={file.id} data-testid={`file-row-${file.id}`}>
                    <TableCell>
                      <FileNameWithStatus>
                        <FileLink
                          href={file.fileUrl || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`file-download-link-${file.id}`}
                        >
                          {file.fileName}
                        </FileLink>
                        {file.quarantineStatus ===
                          QuarantineStatus.Quarantined && (
                          <StatusBadge status={file.quarantineStatus}>
                            QUARANTINED
                          </StatusBadge>
                        )}
                      </FileNameWithStatus>
                    </TableCell>
                    <TableCell>{file.extension}</TableCell>
                    <TableCell>{formatDate(file.createdAt)}</TableCell>
                    <TableCell>{formatLocation(file)}</TableCell>
                    <TableCell>
                      <ActionGroup>
                        {canDeleteFiles && (
                          <DeleteButton
                            onClick={() => handleDeleteFile(file.id)}
                            data-testid={`delete-file-${file.id}`}
                          >
                            Delete
                          </DeleteButton>
                        )}
                      </ActionGroup>
                    </TableCell>
                    {isPortwayEmployee && (
                      <TableCell>
                        <ReviewCheckbox
                          checked={!!file.reviewedByCurrentUser}
                          onChange={() => handleToggleReview(file.id)}
                          title={
                            file.reviewedByCurrentUser
                              ? "Mark as not reviewed"
                              : "Mark as reviewed"
                          }
                          aria-label={
                            file.reviewedByCurrentUser
                              ? "Mark as not reviewed"
                              : "Mark as reviewed"
                          }
                          data-testid={`review-file-${file.id}`}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </tbody>
          </FileTable>
        )}

        {/* Custom delete confirmation dialog */}
        <ConfirmDialog
          isOpen={showDeleteDialog}
          title="File Uploader"
          message={`Are you sure you want to permanently delete "${fileToDelete?.name}"?\n\nThis action cannot be undone.`}
          confirmText="OK"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setFileToDelete(null);
          }}
        />
      </FileListContainer>
    );
  },
);

export default FileList;
